import type { Vec2, Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';

/*
 * Every face here is wound away from a point inside the solid it bounds, whatever order its
 * corners come in, so the painted material's FrontSide culling always shows the outside.
 */

type Quad = readonly [Vec3, Vec3, Vec3, Vec3];
type QuadUv = readonly [Vec2, Vec2, Vec2, Vec2];

const TEMPLATE_FRAME: Frame = { origin: [0, 0, 0], yaw: 0 };

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function scale(a: Vec3, k: number): Vec3 {
  return [a[0] * k, a[1] * k, a[2] * k];
}

function add(...vs: readonly Vec3[]): Vec3 {
  return vs.reduce((s, v): Vec3 => [s[0] + v[0], s[1] + v[1], s[2] + v[2]], [0, 0, 0]);
}

function normalize(a: Vec3): Vec3 {
  return scale(a, 1 / Math.hypot(a[0], a[1], a[2]));
}

function facesAway(outline: readonly Vec3[], inside: Vec3): boolean {
  const [a, b, c] = outline as [Vec3, Vec3, Vec3];
  return dot(cross(sub(b, a), sub(c, a)), sub(a, inside)) > 0;
}

export function outwardQuad(
  builder: MeshBuilder,
  style: VertexStyle,
  corners: Quad,
  inside: Vec3,
  uv?: QuadUv
): void {
  if (facesAway(corners, inside)) {
    builder.quad(style, TEMPLATE_FRAME, corners, uv);
    return;
  }
  const [a, b, c, d] = corners;
  const flippedUv = uv ? ([uv[0], uv[3], uv[2], uv[1]] as const) : undefined;
  builder.quad(style, TEMPLATE_FRAME, [a, d, c, b], flippedUv);
}

export function outwardPolygon(
  builder: MeshBuilder,
  style: VertexStyle,
  outline: readonly Vec3[],
  inside: Vec3
): void {
  const ordered = facesAway(outline, inside) ? outline : [...outline].reverse();
  builder.polygon(style, TEMPLATE_FRAME, ordered);
}

/* Both sides of a thin sheet such as sail cloth, each lit from its own side. */
export function twoSidedQuad(builder: MeshBuilder, style: VertexStyle, corners: Quad): void {
  const [a, b, c, d] = corners;
  builder.quad(style, TEMPLATE_FRAME, corners);
  builder.quad(style, TEMPLATE_FRAME, [a, d, c, b]);
}

export interface WallsOptions {
  readonly floor: boolean;
}

/*
 * Vertical walls around a convex outline of (x, z) points from y0 to y1, with uvs in metres
 * (u along the wall, v up it) so wall and plank patterns keep their real size.
 */
export function walls(
  builder: MeshBuilder,
  style: (width: number) => VertexStyle,
  outline: readonly Vec2[],
  y0: number,
  y1: number,
  options: WallsOptions = { floor: false }
): void {
  const n = outline.length;
  const cx = outline.reduce((s, p) => s + p[0], 0) / n;
  const cz = outline.reduce((s, p) => s + p[1], 0) / n;
  const inside: Vec3 = [cx, (y0 + y1) / 2, cz];
  const h = y1 - y0;
  outline.forEach((p, i) => {
    const q = outline[(i + 1) % n]!;
    const width = Math.hypot(q[0] - p[0], q[1] - p[1]);
    outwardQuad(
      builder,
      style(width),
      [
        [p[0], y0, p[1]],
        [q[0], y0, q[1]],
        [q[0], y1, q[1]],
        [p[0], y1, p[1]],
      ],
      inside,
      [
        [0, 0],
        [width, 0],
        [width, h],
        [0, h],
      ]
    );
  });
  if (options.floor) {
    outwardPolygon(
      builder,
      style(0),
      outline.map((p): Vec3 => [p[0], y0, p[1]]),
      [cx, y0 + 1, cz]
    );
  }
}

export function rectangle(halfX: number, halfZ: number): readonly Vec2[] {
  return [
    [-halfX, -halfZ],
    [halfX, -halfZ],
    [halfX, halfZ],
    [-halfX, halfZ],
  ];
}

export function regularPolygon(radius: number, sides: number): readonly Vec2[] {
  return Array.from({ length: sides }, (_, i): Vec2 => {
    const a = (i / sides) * Math.PI * 2;
    return [Math.cos(a) * radius, Math.sin(a) * radius];
  });
}

/* A closed, square-section beam between two points, such as a trestle bar or a sail stock. */
export function beam(
  builder: MeshBuilder,
  style: VertexStyle,
  from: Vec3,
  to: Vec3,
  halfWidth: number
): void {
  const axis = normalize(sub(to, from));
  const helper: Vec3 = Math.abs(axis[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  const u = normalize(cross(helper, axis));
  const v = cross(axis, u);
  const offsets = [
    add(scale(u, halfWidth), scale(v, halfWidth)),
    add(scale(u, -halfWidth), scale(v, halfWidth)),
    add(scale(u, -halfWidth), scale(v, -halfWidth)),
    add(scale(u, halfWidth), scale(v, -halfWidth)),
  ];
  const inside = scale(add(from, to), 0.5);
  const start = offsets.map((o) => add(from, o));
  const end = offsets.map((o) => add(to, o));
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    outwardQuad(builder, style, [start[i]!, start[j]!, end[j]!, end[i]!], inside);
  }
  outwardPolygon(builder, style, start, inside);
  outwardPolygon(builder, style, end, inside);
}

export interface FrustumShape {
  readonly sides: number;
  readonly radiusBottom: number;
  readonly radiusTop: number;
  readonly y0: number;
  readonly y1: number;
}

/* A tapered many-sided solid on the Y axis; a zero top radius closes it as a cone. */
export function frustum(
  builder: MeshBuilder,
  style: VertexStyle,
  shape: FrustumShape,
  caps: { readonly top: boolean; readonly bottom: boolean }
): void {
  const { sides, radiusBottom, radiusTop, y0, y1 } = shape;
  const inside: Vec3 = [0, (y0 + y1) / 2, 0];
  const ring = (r: number, y: number) => regularPolygon(r, sides).map((p): Vec3 => [p[0], y, p[1]]);
  const bottom = ring(radiusBottom, y0);
  const top = ring(radiusTop, y1);
  for (let i = 0; i < sides; i++) {
    const j = (i + 1) % sides;
    if (radiusTop === 0) {
      outwardPolygon(builder, style, [bottom[i]!, bottom[j]!, [0, y1, 0]], inside);
    } else {
      outwardQuad(builder, style, [bottom[i]!, bottom[j]!, top[j]!, top[i]!], inside);
    }
  }
  if (caps.bottom) outwardPolygon(builder, style, bottom, inside);
  if (caps.top && radiusTop > 0) outwardPolygon(builder, style, top, inside);
}
